import {Collapse, useDisclosure, useTheme, VStack} from '@chakra-ui/react'
import dynamic from 'next/dynamic'
import {useEffect} from 'react'
import ModelExample from '../../dataModels/ModelExample'
import ModelExampleCategory from '../../dataModels/ModelExampleCategory'
import {Model} from '../../dataModels/Service'
import {reportModelContentToSlack} from '../../fetchers/apiFetchers'
import modelStore from '../../stores/modelStore'
import APIOpenModalFooter from '../Modules/APIOpenModalFooter'
import ServiceHeader from '../Modules/ServiceHeader'
import ModelInput from './ModelInput'
import {COOKIE_INPUTS} from '../../constants/cookies'
import {parseCookies} from 'nookies'
import {deleteCookieClient} from '../../utils/cookieHelpers'
import useTranslation from 'next-translate/useTranslation'
import {SUPPORTED_MODELS} from '../../constants/models'
import ServiceGroupButtons from '../ServiceGroup/ServiceGroupButtons'

const ToxicWarning = dynamic(() => import('../Modules/ToxicWarning'))
const ModelExamplesModal = dynamic(() => import('../Modals/ModelExamplesModal'))

interface Props {
  modelExamples: ModelExample[]
  modelExampleCategories: ModelExampleCategory[]
  model: Model
}

const ModelPlayground = ({modelExamples, modelExampleCategories, model}: Props) => {
  const modelExamplesModal = useDisclosure()
  const toxicWarning = useDisclosure()

  const theme = useTheme()

  const {t} = useTranslation('model')
  const isToxic = modelStore(state => state.isToxic)
  const resetPlayground = modelStore(state => state.resetPlayground)

  const setTemperature = modelStore(state => state.setTemperature)
  const setTopP = modelStore(state => state.setTopP)
  const setInput = modelStore(state => state.setInput)
  const setDisplayOptions = modelStore(state => state.setDisplayOptions)
  const setMaxLength = modelStore(state => state.setMaxLength)

  //@ts-ignore
  // TODO
  const text = t(model.description)

  useEffect(() => {
    resetPlayground()
    const {presentInputs} = parseCookies()
    if (presentInputs) {
      const presentPlaygroundValues = JSON.parse(presentInputs)
      setInput(presentPlaygroundValues.input)
      setTemperature(presentPlaygroundValues.temperature)
      setTopP(presentPlaygroundValues.topP)
      setMaxLength(presentPlaygroundValues.maxLength)
      setDisplayOptions(true)
    }
    deleteCookieClient(COOKIE_INPUTS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isToxic) {
      toxicWarning.onOpen()
    } else {
      toxicWarning.onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToxic])

  return (
    <VStack w='full' spacing={10} pb={5}>
      <ModelExamplesModal
        isOpen={modelExamplesModal.isOpen}
        onClose={modelExamplesModal.onClose}
        modelExamples={modelExamples}
        modelExamplesCategories={modelExampleCategories}
      />
      <ServiceHeader serviceGroup={SUPPORTED_MODELS} />
      <ServiceGroupButtons serviceGroup={SUPPORTED_MODELS} />
      <VStack
        w='full'
        p={5}
        spacing={5}
        background={theme.colors.layers.appLevel1}
        borderRadius={theme.radii.big}
        justify='start'>
        <ModelInput
          model={model}
          modelExamples={modelExamples}
          modelExamplesCategories={modelExampleCategories}
        />
        {toxicWarning.isOpen && (
          <Collapse in={toxicWarning.isOpen} unmountOnExit animateOpacity>
            <ToxicWarning
              onClose={toxicWarning.onClose}
              reportContentToSlack={reportModelContentToSlack}
            />
          </Collapse>
        )}
        <APIOpenModalFooter service={model} serviceGroup={SUPPORTED_MODELS} />
      </VStack>
    </VStack>
  )
}

export default ModelPlayground
